import { type FC, useState } from 'react'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import useAssignableJobRoles from 'modules/job_catalog/hooks/useAssignableJobRoles'
import jobLevelsCollection, {
  jobRoleDefaultLevel,
  jobRoleHasAssignableLevels,
  jobRoleLevels,
} from 'modules/job_catalog/store/collections/levels'
import type JobLevel from 'modules/job_catalog/store/models/JobLevel'
import type JobRole from 'modules/job_catalog/store/models/JobRole'

interface Props {
  onClose: () => void
  onSave: (role: JobRole, level: JobLevel) => void
  employees: Employee[]
}

const RoleAndLevelSelectModal: FC<Props> = ({ onClose, onSave, employees }) => {
  const [role, setRole] = useState<JobRole>()
  const [level, setLevel] = useState<JobLevel>()
  const [levelSelectorOpened, setLevelSelectorOpened] = useState(false)
  const employeesLegalEntityIds = uniq(employees.map((x) => employeeLegalEntityId(x)))
  const { assignableJobRoles: roles } = useAssignableJobRoles(employeesLegalEntityIds)

  const roleError =
    !levelSelectorOpened && role && jobRoleLevels(role) && !level
      ? i18n.t('job_catalog.select_a_level_error')
      : ''

  const handleSave = () => {
    if (role && level) {
      onSave(role, level)
    }
  }

  const handleSetRole = (id: string) => {
    const nextRole = roles.find((role) => role.attributes.id === id)
    setRole(nextRole)
    if (nextRole && !jobRoleHasAssignableLevels(nextRole)) {
      setLevel(jobRoleDefaultLevel(nextRole))
    } else {
      setLevel(undefined)
      setLevelSelectorOpened(true)
    }
  }

  const handleSetLevel = (id: string) => {
    const nextLevel = jobLevelsCollection.get(id)
    setLevel(nextLevel)
    setLevelSelectorOpened(false)
  }

  const rolesOptions = roles.map((role) => {
    const { id, name } = role.attributes

    return (
      <Selector.Option key={id} value={id} label={name} searchValue={name}>
        {name}
      </Selector.Option>
    )
  })

  const levelsOptions = role
    ? jobRoleLevels(role).map((level) => {
        const { id, name } = level.attributes
        return (
          <Selector.Option key={id} value={id} label={name} searchValue={name}>
            {name}
          </Selector.Option>
        )
      })
    : []

  const handleLevelSelectorToggle = () => {
    setLevelSelectorOpened(!levelSelectorOpened)
  }

  const buttonDisabled = role && !level

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('job_catalog.bulk_edit.edit_modal_title')}
      description={i18n.t('job_catalog.bulk_edit.edit_modal_description')}
      onSubmit={handleSave}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={buttonDisabled} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Selector.Popover
          label={i18n.t('job_catalog.bulk_edit.job_role')}
          headerLabel={i18n.t('common.name')}
          blankLabel={i18n.t('common.empty')}
          value={role?.attributes?.id ? [role?.attributes?.id] : []}
          onChange={(x) => handleSetRole(x as string)}
          placeholder={i18n.t('job_catalog.choose_role')}
          searchPlaceholder={i18n.t('common.search')}
          error={roleError}
        >
          {rolesOptions || []}
        </Selector.Popover>
        <Selector.Popover
          label={i18n.t('job_catalog.bulk_edit.job_level')}
          headerLabel={i18n.t('common.name')}
          blankLabel={i18n.t('common.empty')}
          value={level?.isDefault || !level ? [] : [level?.attributes?.id]}
          isDisabled={!role || !jobRoleLevels(role).length}
          onChange={(x) => handleSetLevel(x as string)}
          placeholder={
            level && level.isDefault
              ? i18n.t('job_catalog.level_not_available')
              : i18n.t('job_catalog.choose_level')
          }
          searchPlaceholder={i18n.t('common.search')}
          mandatory={!!role && !level?.isDefault}
          isOpen={levelSelectorOpened}
          onToggle={handleLevelSelectorToggle}
        >
          {levelsOptions || []}
        </Selector.Popover>
        <Alert color="warning">
          <Text.Normal>{i18n.t('job_catalog.bulk_edit.edit_modal_alert')}</Text.Normal>
        </Alert>
      </Box>
    </Modal>
  )
}

export default observer(RoleAndLevelSelectModal)
